import React, { useState } from 'react';
import {
  Card, Form, Button, Alert, Spinner, Modal, Toast, Badge, Row, Col, InputGroup,
} from 'react-bootstrap';
import { Bell, AlertCircle, Copy, CheckCircle2, ShieldCheck, Download } from 'lucide-react';
import useAuthHeaders from '../../hooks/useAuthHeaders';
import RequestViewer from './RequestViewer';
import { buildFielFormData } from '../../utils/fielCrypto';
import { apiFetch, ensureOk } from '../../utils/apiError';

const API_URL_V2 = process.env.REACT_APP_API_URL_V2;

const NotificacionesTab = ({ credentials, isValid }) => {
  const { getToken } = useAuthHeaders();
  const isFiel = credentials.authMode === 'fiel';
  const vaultRegistered = credentials.signingKeyStatus?.isRegistered === true;

  const [requestId, setRequestId]                   = useState('');
  const [tipoNotificacion, setTipoNotificacion]     = useState('notificadas');
  const [descargarNotificaciones, setDescargar]     = useState(false);
  const [loading, setLoading]                       = useState(false);
  const [resultado, setResultado]                   = useState(null);
  const [lastRequestId, setLastRequestId]           = useState('');
  const [copied, setCopied]                         = useState(false);
  const [errorMsg, setErrorMsg]                     = useState('');
  const [showError, setShowError]                   = useState(false);
  const [showToast, setShowToast]                   = useState(false);
  const [lastRequest, setLastRequest]               = useState(null);

  const [vaultSaving, setVaultSaving]   = useState(false);
  const [vaultSaveOk, setVaultSaveOk]   = useState(false);
  const [vaultSaveErr, setVaultSaveErr] = useState('');


  const downloadBlob = (base64, fileName) => {
    const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: 'application/pdf' });
    const url   = URL.createObjectURL(blob);
    const link  = Object.assign(document.createElement('a'), { href: url, download: fileName });
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || !isFiel) return;
    setLoading(true);
    setResultado(null);

    try {
      const token  = await getToken();
      const params = new URLSearchParams({ tipoNotificacion, descargarNotificaciones });
      if (requestId.trim()) params.set('requestId', requestId.trim());

      const { formData, headers: fielHeaders } = await buildFielFormData(credentials, token);
      const res = await apiFetch(`${API_URL_V2}/Consultar/notificacionesfiel?${params}`, {
        method:  'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Rfc': credentials.rfc, ...fielHeaders },
        body:    formData,
      });

      await ensureOk(res);

      const data = await res.json();
      if (!data.success) throw new Error(data.errorMessage || 'No se pudieron obtener las notificaciones.');

      setResultado(data);
      if (data.requestId) {
        setLastRequestId(data.requestId);
        setRequestId(data.requestId);
      }
      setShowToast(true);

      setLastRequest({
        method:      'POST',
        baseUrl:     `${API_URL_V2}/Consultar/notificacionesfiel`,
        queryParams: params.toString(),
        headers:     { 'Rfc': credentials.rfc },
        formFields: vaultRegistered ? null : [
          { name: 'llavePrivada', type: 'file', fileName: credentials.privateKeyFile?.name || 'llave.key',      varName: 'llavePrivadaFile' },
          { name: 'Certificado',  type: 'file', fileName: credentials.certFile?.name     || 'certificado.cer', varName: 'certificadoFile' },
          { name: 'Contrasena',   type: 'text', value: '{contrasena_fiel}' },
        ],
      });

      if (!vaultRegistered && credentials.saveToVault && credentials.onStoreSigningKey) {
        setVaultSaving(true);
        setVaultSaveOk(false);
        setVaultSaveErr('');
        const result = await credentials.onStoreSigningKey();
        setVaultSaving(false);
        if (result.success) setVaultSaveOk(true);
        else setVaultSaveErr(result.error || 'No se pudo guardar la FIEL en Key Vault.');
      }

    } catch (err) {
      setErrorMsg(err.message || 'Error al obtener notificaciones.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const copyRequestId = async () => {
    try { await navigator.clipboard.writeText(lastRequestId); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Card className="tab-section-card border-0 shadow-sm">
        <Card.Body>

          {!isFiel && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Este endpoint solo está disponible con <strong>FIEL</strong>. Cambia el modo de autenticación arriba.</small>
            </Alert>
          )}

          {isFiel && !isValid && (
            <Alert variant="warning" className="d-flex align-items-center py-2 mb-3">
              <AlertCircle size={16} className="me-2 flex-shrink-0" />
              <small>Completa las credenciales FIEL de arriba para habilitar la consulta.</small>
            </Alert>
          )}

          <Form onSubmit={handleSubmit}>

            {/* ── Request ID ──────────────────────── */}
            <Row className="mb-3">
              <Col md={8}>
                <Form.Label className="form-label-modern">
                  Request ID{' '}
                  <span className="text-muted fw-normal small">
                    (opcional — reutiliza una sesión SAT existente)
                  </span>
                </Form.Label>
                <InputGroup>
                  <Form.Control className="form-control-modern" type="text"
                    placeholder="Pega el requestId de una consulta anterior"
                    value={requestId} onChange={(e) => setRequestId(e.target.value)}
                    style={{ borderRadius: '10px 0 0 10px' }} />
                  {requestId && (
                    <Button variant="outline-secondary" onClick={() => setRequestId('')}
                      style={{ borderRadius: '0 10px 10px 0' }}>✕</Button>
                  )}
                </InputGroup>
              </Col>
            </Row>

            {/* ── Parámetros ───────────────────────── */}
            <div className="p-3 mb-3" style={{
              background: 'linear-gradient(135deg,rgba(19,160,255,.05),rgba(0,212,170,.05))',
              borderRadius: 12, border: '1px solid rgba(19,160,255,.12)',
            }}>
              <Row className="g-3 align-items-end">
                <Col md={4}>
                  <Form.Label className="form-label-modern">Tipo de notificación</Form.Label>
                  <div className="d-flex gap-3 mt-1">
                    <Form.Check type="radio" id="tipo-notificadas" label="Notificadas"
                      className="custom-radio" checked={tipoNotificacion === 'notificadas'}
                      onChange={() => setTipoNotificacion('notificadas')} />
                    <Form.Check type="radio" id="tipo-pendientes" label="Pendientes"
                      className="custom-radio" checked={tipoNotificacion === 'pendientes'}
                      onChange={() => setTipoNotificacion('pendientes')} />
                  </div>
                </Col>
                <Col md={5}>
                  <Form.Label className="form-label-modern">Descarga de PDFs</Form.Label>
                  <div className="d-flex gap-3 mt-1">
                    <Form.Check type="radio" id="desc-false" label="Solo metadata"
                      className="custom-radio" checked={!descargarNotificaciones}
                      onChange={() => setDescargar(false)} />
                    <Form.Check type="radio" id="desc-true" label="Con PDFs"
                      className="custom-radio" checked={descargarNotificaciones}
                      onChange={() => setDescargar(true)} />
                  </div>
                </Col>
              </Row>
            </div>

            <Button type="submit" className="btn-gradient" disabled={!isValid || !isFiel || loading}>
              {loading
                ? <><Spinner animation="border" size="sm" className="me-2" />Consultando…</>
                : <><Bell size={18} className="me-2" />Obtener Notificaciones</>
              }
            </Button>

          </Form>

          {/* ── Request ID banner ────────────────── */}
          {lastRequestId && (
            <div className="mt-3 p-3 d-flex align-items-center gap-3 flex-wrap"
              style={{ background: 'rgba(0,212,170,.07)',
                border: '1px solid rgba(0,212,170,.3)', borderRadius: 10 }}>
              <CheckCircle2 size={18} color="#00d4aa" className="flex-shrink-0" />
              <div className="flex-grow-1">
                <div className="text-muted small mb-1">
                  Request ID — se reutilizará automáticamente en la próxima consulta:
                </div>
                <code style={{ fontSize: '.85rem', color: '#1a1f3a',
                  wordBreak: 'break-all', background: 'transparent' }}>
                  {lastRequestId}
                </code>
              </div>
              <Button variant="outline-success" size="sm" onClick={copyRequestId}
                className="d-flex align-items-center gap-1 flex-shrink-0">
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiado' : 'Copiar'}
              </Button>
            </div>
          )}

          {resultado && (
            <div className="mt-4">
              <NotificacionesList data={resultado} onDownload={downloadBlob} />
            </div>
          )}

          <RequestViewer request={lastRequest} />
        </Card.Body>
      </Card>

      <Toast show={showToast} onClose={() => setShowToast(false)} delay={4000} autohide
        className="custom-toast toast-success">
        <Toast.Header>
          <CheckCircle2 size={16} className="me-2 text-success" />
          <strong className="me-auto">Notificaciones obtenidas</strong>
        </Toast.Header>
        <Toast.Body>Se encontraron {resultado?.totalRegistros ?? 0} notificaciones.</Toast.Body>
      </Toast>

      <Toast show={vaultSaving} className="custom-toast"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header closeButton={false}>
          <Spinner animation="border" size="sm" className="me-2" />
          <strong className="me-auto">Guardando en Key Vault…</strong>
        </Toast.Header>
      </Toast>

      <Toast show={vaultSaveOk} onClose={() => setVaultSaveOk(false)} delay={5000} autohide
        className="custom-toast toast-success"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <ShieldCheck size={14} className="me-2 text-success" />
          <strong className="me-auto">FIEL guardada en Key Vault</strong>
        </Toast.Header>
        <Toast.Body>Ya no necesitarás subir los archivos FIEL para este RFC.</Toast.Body>
      </Toast>

      <Toast show={!!vaultSaveErr} onClose={() => setVaultSaveErr('')} delay={7000} autohide
        className="custom-toast toast-error"
        style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
        <Toast.Header>
          <AlertCircle size={14} className="me-2 text-danger" />
          <strong className="me-auto">No se pudo guardar en Key Vault</strong>
        </Toast.Header>
        <Toast.Body style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{vaultSaveErr}</Toast.Body>
      </Toast>

      <Modal show={showError} onHide={() => setShowError(false)} centered>
        <Modal.Header closeButton style={{ borderBottom: '3px solid #ff6b6b' }}>
          <Modal.Title className="d-flex align-items-center gap-2" style={{ color: '#1a1f3a' }}>
            <AlertCircle size={20} color="#ff6b6b" />Error al obtener notificaciones
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0" style={{ color: '#4a5568', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorMsg}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowError(false)}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const NotificacionesList = ({ data, onDownload }) => (
  <div>
    {/* Resumen de paginación */}
    <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
      <strong style={{ color: '#1a1f3a' }}>
        <Bell size={16} className="me-1" />
        {data.totalRegistros} notificación{data.totalRegistros !== 1 ? 'es' : ''}
      </strong>
      {data.totalPaginas > 1 && (
        <Badge bg="secondary" style={{ fontWeight: 400 }}>
          Pág. {data.paginaActual} / {data.totalPaginas}
        </Badge>
      )}
    </div>

    {data.notificaciones?.length === 0 && (
      <Alert variant="info" className="py-2 mb-0">
        <small>No se encontraron notificaciones para este RFC.</small>
      </Alert>
    )}

    <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
      {data.notificaciones?.map((n, i) => (
        <div key={n.rowKey || i} style={{
          background: '#fff', borderRadius: 10, padding: '.75rem 1rem',
          border: '1px solid rgba(19,160,255,.15)',
        }}>
          <div className="d-flex align-items-start gap-2 flex-wrap">
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              <Row className="g-1 small">
                <Col xs={6} md={3}>
                  <span className="text-muted d-block" style={{ fontSize: '.72rem' }}>Folio</span>
                  <strong style={{ fontSize: '.82rem' }}>{n.folio || '—'}</strong>
                </Col>
                <Col xs={6} md={2}>
                  <span className="text-muted d-block" style={{ fontSize: '.72rem' }}>Fecha</span>
                  <span style={{ fontSize: '.82rem' }}>{n.fecha || '—'}</span>
                </Col>
                <Col xs={12} md={4}>
                  <span className="text-muted d-block" style={{ fontSize: '.72rem' }}>Autoridad</span>
                  <span style={{ fontSize: '.82rem' }}>{n.autoridad || '—'}</span>
                </Col>
                <Col xs={12} md={3}>
                  <span className="text-muted d-block" style={{ fontSize: '.72rem' }}>Acto</span>
                  <span style={{ fontSize: '.82rem' }}>{n.acto || '—'}</span>
                </Col>
              </Row>
              {n.errorDescargaPdf && (
                <small className="text-danger d-block mt-1" style={{ fontSize: '.72rem' }}>
                  {n.errorDescargaPdf}
                </small>
              )}
            </div>
            <div className="d-flex flex-column gap-1 flex-shrink-0">
              {n.pdfContent && (
                <Button variant="outline-primary" size="sm"
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}
                  onClick={() => onDownload(n.pdfContent, n.pdfFileName || `notificacion-${n.folio || i}.pdf`)}>
                  <Download size={12} />PDF
                </Button>
              )}
              {n.actoAdministrativoRow?.pdfContent && (
                <Button variant="outline-secondary" size="sm"
                  className="d-flex align-items-center gap-1"
                  style={{ fontSize: '.73rem', whiteSpace: 'nowrap' }}
                  onClick={() => onDownload(
                    n.actoAdministrativoRow.pdfContent,
                    n.actoAdministrativoRow.pdfFileName || `acto-${n.folio || i}.pdf`,
                  )}>
                  <Download size={12} />Acto
                </Button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default NotificacionesTab;
